import { useState } from 'react';
import AddHeadlineModal from '../../modals/AddHeadlineModal';
import axiosService from '../../../utils/axios';
import { useDispatch, useSelector } from 'react-redux';
import authSlice from '../../../store/slices/auth';
import JWButton from '../../brand/JWButton';
import { PencilEditIcon } from '../../icons';
import { ProfileFieldHint } from '../ProfileHint';

export interface HeadlineType {
    _id: string
    title: string
    skill: string
}

interface HeadlineProps {
    selectedHeadline?: Array<HeadlineType> | undefined | null
    setSelectedHeadline?: (skills: Array<HeadlineType>) => void
    saveHeadline?: () => Promise<void>
    isLoading?: boolean
    canEdit?: boolean
    profileData?: any
    hintActive?: boolean
    onDismissHint?: () => void
    onHeadlineSaved?: (headline: string) => void
}

function Headline({ canEdit = false, profileData, hintActive = false, onDismissHint, onHeadlineSaved }: HeadlineProps) {
    const profile = useSelector((state: any) => state.auth.profile);
    const user = useSelector((state: any) => state.auth.user);

    const dispatch = useDispatch();
    const [showHeadlineModal, setShowHeadlineModal] = useState(false);

    // Use the profileData if provided (for viewing other users), otherwise fall back to current user's profile
    const displayProfile = profileData || profile;

    const saveHeadline = async (headlineValue: string) => {
        const { data } = await axiosService.put(`/profile/${profile._id}?userId=${user._id}`, {
            headline: headlineValue,
        });

        // to live update the state
        setHeadlineObj({ type: headlineValue.split(' ')[0], content: headlineValue.substring(headlineValue.indexOf(' ') + 1) });
        setShowHeadlineModal(false);
        dispatch(authSlice.actions.setProfile(data));
        onHeadlineSaved?.(typeof data?.headline === 'string' ? data.headline : headlineValue);
    };

    // Parse headline to separate type and content
    const parseHeadline = (headline: string) => {
        // Check if headline starts with one of the known types
        const headlineTypes = ['Open To', 'Seeking', 'Offering'];
        let type = '';
        let content = headline;

        for (const headlineType of headlineTypes) {
            if (headline.startsWith(headlineType)) {
                type = headlineType;
                content = headline.substring(headlineType.length).trim();
                break;
            }
        }

        return { type, content };
    };

    // Get the headline parts
    const headlineData = displayProfile?.headline ? parseHeadline(displayProfile.headline) : { type: '', content: '' };

    // used as local state to forces state update - redux store not updating in component properly
    const [headlineObj, setHeadlineObj] = useState(headlineData);

    // Common headline classes for both editable and non-editable states
    const headlineClasses = 'w-full text-center headline-text px-4 py-1.5 bg-purple-700 text-white text-sm sm:text-sm md:text-sm lg:text-base xl:text-lg truncate font-nunito font-semibold flex justify-center items-center h-[38px]';

    // Type label classes
    const typeClasses = 'w-full text-center headline-type px-4 py-1.5 bg-purple-900 text-white text-xs font-bold uppercase tracking-wide rounded-t-lg font-nunito';

    // Content classes when there's a type above - flat top, rounded bottom
    const contentClasses = headlineClasses;

    // Other user's headline content - slightly less height
    const otherUserContentClasses = 'w-full text-center headline-text px-4 py-1.5 bg-purple-700 text-white text-sm sm:text-sm md:text-sm lg:text-base xl:text-lg truncate font-nunito font-semibold flex justify-center items-center h-[38px]';

    // Container class with extra right padding on mobile
    const containerClasses = 'flex flex-col w-full pr-3 sm:pr-3 md:pr-0';

    // Ensure consistent height for the container
    const wrapperClasses = 'flex flex-col w-full min-h-[40px]';

    // Determine if we should show the top type section
    const hasTypeSection = profile?.headline ? headlineObj.type : true; // Always true for new users (shows "Pick")

    // Determine button style based on if type section is shown
    const buttonRoundedStyle = hasTypeSection ? 'rounded-b-lg rounded-t-none' : 'rounded-lg';

    return (
        <div className={wrapperClasses}>
            <div className={containerClasses}>
                {canEdit ?
                    (
                        <>
                            {profile?.headline ? (
                                headlineObj.type && (
                                    <div className={typeClasses}>
                                        {headlineObj.type}
                                    </div>
                                )
                            ) : (
                                <div className={typeClasses}>
                                    Pick
                                </div>
                            )}
                            <ProfileFieldHint id="headline" active={hintActive} onDismiss={onDismissHint ?? (() => undefined)}>
                                <JWButton
                                    onClick={() => setShowHeadlineModal(true)}
                                    className={`${contentClasses} ${buttonRoundedStyle}`}
                                >
                                    {profile?.headline ? (
                                        headlineObj.content
                                    ) : (
                                        <div className="relative w-full">
                                            <div className="absolute right-2 top-1/2 -translate-y-1/2">
                                                <PencilEditIcon
                                                    width={16}
                                                    height={16}
                                                    fill="currentColor"
                                                    className="opacity-80"
                                                />
                                            </div>
                                            <div className="text-center">
                                                Add a headline
                                            </div>
                                        </div>
                                    )}
                                </JWButton>
                            </ProfileFieldHint>
                        </>
                    ) : (
                    // Display the headline as text with the same styles as JWButton
                        displayProfile?.headline && (
                            <>
                                {headlineObj.type && (
                                    <div className={typeClasses}>
                                        {headlineObj.type}
                                    </div>
                                )}
                                <div className={`${otherUserContentClasses} ${headlineObj.type ? 'rounded-b-lg rounded-t-none' : 'rounded-lg'}`}>
                                    {headlineObj.content}
                                </div>
                            </>
                        )
                    )}
            </div>
            <AddHeadlineModal
                show={showHeadlineModal}
                setShow={setShowHeadlineModal}
                onClose={() => setShowHeadlineModal(false)}
                onSave={saveHeadline}
            />
        </div>
    );
}

export default Headline;
